import { defineStore } from 'pinia'
import type { BookBubble } from '~/stores/interfaces'
import { log } from '@/composables/useLogger'

// Helper function to safely extract year from date string (YYYY-MM-DD or just YYYY)
function getYear(dateString: string | null | undefined): number | null {
  if (!dateString) return null
  const yearMatch = dateString.match(/^(\d{4})/)
  return yearMatch ? parseInt(yearMatch[1], 10) : null
}

export const useBookStore = defineStore('book', {
  state: () => ({
    searchQuery: '',
    selectedAuthor: 'All',
    selectedCategory: 'All',
    selectedPublisher: 'All',
    selectedReadStatus: 'All' as 'All' | 'Read' | 'Unread',
    minPublicationYear: null as number | null,
    maxPublicationYear: null as number | null,
    books: [] as BookBubble[],
  }),

  getters: {
    filteredBooks(): BookBubble[] {
      return this.books
        .filter((book) => {
          const matchesSearch = book.title.toLowerCase().includes(this.searchQuery.toLowerCase())
          const matchesAuthor =
            this.selectedAuthor === 'All' || (book.authors?.includes(this.selectedAuthor) ?? false)
          const matchesCategory =
            this.selectedCategory === 'All' ||
            (book.categories?.includes(this.selectedCategory) ?? false)
          const matchesPublisher =
            this.selectedPublisher === 'All' || book.publisher === this.selectedPublisher

          const matchesReadStatus =
            this.selectedReadStatus === 'All' ||
            (this.selectedReadStatus === 'Read' && book.read) ||
            (this.selectedReadStatus === 'Unread' && !book.read)

          const publicationYear = getYear(book.publicationDate)
          const matchesMinYear =
            this.minPublicationYear === null ||
            (publicationYear !== null && publicationYear >= this.minPublicationYear)
          const matchesMaxYear =
            this.maxPublicationYear === null ||
            (publicationYear !== null && publicationYear <= this.maxPublicationYear)

          return (
            matchesSearch &&
            matchesAuthor &&
            matchesCategory &&
            matchesPublisher &&
            matchesReadStatus &&
            matchesMinYear &&
            matchesMaxYear
          )
        })
        .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    },
    uniqueAuthors(): string[] {
      const authors = this.books.flatMap((book) => book.authors ?? [])
      return ['All', ...Array.from(new Set(authors)).sort()]
    },
    uniqueCategories(): string[] {
      const categories = this.books.flatMap((book) => book.categories ?? [])
      return ['All', ...Array.from(new Set(categories)).sort()]
    },
    uniquePublishers(): string[] {
      const publishers = this.books.map((book) => book.publisher).filter(Boolean) as string[]
      return ['All', ...Array.from(new Set(publishers)).sort()]
    },
    publicationYearRange(): { min: number | null; max: number | null } {
      let min: number | null = null
      let max: number | null = null
      this.books.forEach((book) => {
        const year = getYear(book.publicationDate)
        if (year !== null) {
          if (min === null || year < min) min = year
          if (max === null || year > max) max = year
        }
      })
      return { min, max }
    },
  },

  actions: {
    setSearchQuery(query: string) {
      this.searchQuery = query
    },
    setFilters(filters: {
      author?: string
      category?: string
      publisher?: string
      readStatus?: 'All' | 'Read' | 'Unread'
      minYear?: number | null
      maxYear?: number | null
    }) {
      if (filters.author !== undefined) this.selectedAuthor = filters.author
      if (filters.category !== undefined) this.selectedCategory = filters.category
      if (filters.publisher !== undefined) this.selectedPublisher = filters.publisher
      if (filters.readStatus !== undefined) this.selectedReadStatus = filters.readStatus
      if (filters.minYear !== undefined) this.minPublicationYear = filters.minYear
      if (filters.maxYear !== undefined) this.maxPublicationYear = filters.maxYear
    },
    setPublisher(publisher: string) {
      this.selectedPublisher = publisher
    },
    setReadStatus(status: 'All' | 'Read' | 'Unread') {
      this.selectedReadStatus = status
    },
    setPublicationYearRange(minYear: number | null, maxYear: number | null) {
      this.minPublicationYear = minYear
      this.maxPublicationYear = maxYear
    },
    resetFilters() {
      this.searchQuery = ''
      this.selectedAuthor = 'All'
      this.selectedCategory = 'All'
      this.selectedPublisher = 'All'
      this.selectedReadStatus = 'All'
      this.minPublicationYear = null
      this.maxPublicationYear = null
    },
    getRandomBook(): BookBubble | null {
      if (this.books.length === 0) return null
      const randomIndex = Math.floor(Math.random() * this.books.length)
      return this.books[randomIndex] as BookBubble
    },
    setBooks(books: BookBubble[]) {
      log(
        'BookStore - Setting books:',
        books.map((b) => ({
          uuid: b.uuid,
          read: b.meta?.read,
          meta: b.meta,
        }))
      )
      this.books = books
    },
    updateBook(updatedBook: BookBubble) {
      log('BookStore - Updating book:', {
        uuid: updatedBook.uuid,
        oldState: this.books.find((b) => b.uuid === updatedBook.uuid)?.meta?.read,
        newState: updatedBook.meta?.read,
        oldMeta: this.books.find((b) => b.uuid === updatedBook.uuid)?.meta,
        newMeta: updatedBook.meta,
      })

      // Create a new array to ensure reactivity
      this.books = this.books.map((book) =>
        book.uuid === updatedBook.uuid ? { ...updatedBook } : book
      )
    },
    removeBook(uuid: string) {
      this.books = this.books.filter((book) => book.uuid !== uuid)
    },
  },
})
